<script context="module">
  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';

  const collectionProductQuery = gql`
    query ProductsByCategoryAndCollection($collection: String!, $category: String!) {
      Product_ProductCollections(
        where: {
          ProductCollection: { slug: { _eq: $collection }, ProductCategory: { slug: { _eq: $category } } }
          visible: { _eq: true }
          Product: { visible: { _eq: true } }
        }
        order_by: { Product: { ProductType: { id: asc } }, sortOrder: desc_nulls_last }
      ) {
        id
        name
        slug
        Product {
          id
          price
          displayColourPath
          displayColour
          mensAvailable
          womensAvailable
          youthAvailable
          slug
          isModelBasedImages
          useCoverImageForPreview
          isSublimationProduct
          isBulkyProduct
          ProductType {
            id
            name
          }
          Product_Colours(
            order_by: { ProductColoursMasterMain: { colourFamilyId: asc } }
            where: { visible: { _eq: true } }
          ) {
            id
            displayName
            pathName
            mens
            womens
            youth
            ProductColoursMasterMain {
              id
              rgb
              name
            }
            ProductColoursMasterTrim1 {
              id
              name
              rgb
            }
            ProductColoursMasterTrim2 {
              id
              name
              rgb
            }
            ProductColoursMasterTrim3 {
              id
              name
              rgb
            }
          }
          Product_SublimationTemplates(where: { isVisible: { _eq: true } }) {
            id
            isDefault
            isVisible
            SublimationTemplate {
              id
              name
              numberOfColours
            }
          }
          BulkPricingDiscounts(order_by: { minQty: asc }) {
            id
            minQty
            maxQty
            discountPercent
          }
          Supplier {
            id
            displayName
          }
        }
        ProductCollection {
          id
          ProductCategory {
            id
            name
          }
        }
      }
    }
  `;

  export async function preload(page) {
    const { category, collection } = page.params;

    // Now we need to fetch data for the particular collection
    let collectionProductsRaw = await client.query({
      query: collectionProductQuery,
      variables: {
        collection: collection,
        category: category,
      },
    });

    let products = [];

    // Now process the product data for the popular products section
    for (const item of collectionProductsRaw.data.Product_ProductCollections) {
      // determine genders
      let genders = [];
      if (item.Product.mensAvailable) {
        genders.push('mens');
      }
      if (item.Product.womensAvailable) {
        genders.push('womens');
      }
      if (item.Product.youthAvailable) {
        genders.push('youth');
      }

      // Pass the entire colour object over to Products component, rather than image url
      // That way we can use the same changeColour function to set images
      let defaultImageColourObject;

      // Here we are checking if the display colour exists in the product colour data
      // If it does, we will use that as the default image
      let displayColourSearchArray = item.Product.Product_Colours.filter(
        (colour) => colour?.pathName?.toLowerCase() === item.Product?.displayColourPath?.toLowerCase(),
      );
      if (displayColourSearchArray.length > 0) {
        defaultImageColourObject = displayColourSearchArray[0];
      } else {
        defaultImageColourObject = item.Product.Product_Colours[0];
      }

      products.push({
        id: item.Product.id,
        type: item.Product.ProductType.name,
        name: item.name,
        slug: item.slug,
        rootSlug: item.Product.slug,
        price: item.Product.price,
        category: item.ProductCollection.ProductCategory.name,
        genders: genders,
        imageColourObject: defaultImageColourObject,
        colours: item.Product.Product_Colours,
        isModelBasedImages: item.Product.isModelBasedImages,
        useCoverImageForPreview: item.Product.useCoverImageForPreview,
        isSublimationProduct: item.Product.isSublimationProduct,
        isBulkyProduct: item.Product.isBulkyProduct,
        sublimationTemplates: item.Product.Product_SublimationTemplates,
        supplier: item.Product.Supplier?.displayName,
        bulkDiscounts: item.Product.BulkPricingDiscounts,
      });
    }

    const sublimationProducts = products.filter((product) => product.isSublimationProduct);
    const nonSublimationProducts = products.filter((product) => !product.isSublimationProduct);

    return {
      category,
      collection,
      sublimationProducts,
      nonSublimationProducts,
    };
  }
</script>

<script>
  import { MetaTags } from 'svelte-meta-tags';

  import PopularProducts from '../../../../layouts/home/PopularProducts.svelte';

  export let category;
  export let collection;
  export let sublimationProducts;
  export let nonSublimationProducts;

  $: collectionTitle = collection.charAt(0).toUpperCase() + collection.slice(1);
</script>

<MetaTags
  title="{collectionTitle} Products"
  titleTemplate="%s | Uniform Hero"
  description="Huge selection of {collection} products covering all {category} needs. Find your product and customise with names, numbers and logos, using our online design hub."
  canonical="https://uniformhero.com.au/products/{category}/{collection}"
  openGraph={{
    url: `https://uniformhero.com.au/products/${category}`,
    title: 'Uniform Hero - The best selection of sports and uniform products',
    description: 'Order custom uniforms online in 5 easy steps. Fast delivery. Top quality products.',
    images: [
      {
        url: 'https://images.uniformhero.com.au/assets/opengraph-banner.jpg',
        width: 1200,
        height: 630,
        alt: 'Custom printed uniforms',
      },
    ],
    site_name: 'Uniform Hero',
    type: 'website',
  }}
/>

<!-- Need this to make the title reactive - meta tags doesn't work -->
<svelte:head>
  <title>{collectionTitle} Products | Uniform Hero</title>
</svelte:head>

<div class="products">
  {#if sublimationProducts.length > 0}
    <PopularProducts products={sublimationProducts} title={'Fully Custom Products'} isSublimation={true} />
    <div class="spacer"></div>
  {/if}
  <PopularProducts products={nonSublimationProducts} title={'Classic Products'} />
</div>

<style>
  .spacer {
    height: 4rem;
  }
</style>
